import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { AppearancePreference } from "@/design-system/generated/theme-metadata";

const APPEARANCE_KEY = "sanchezcloud.appearance";
const LEGACY_PREFERENCES_KEY = "sanchezcloud.preferences.v1";

function isAppearance(value: unknown): value is AppearancePreference {
  return value === "system" || value === "light" || value === "dark";
}

function readAppearance(): AppearancePreference {
  try {
    const saved = localStorage.getItem(APPEARANCE_KEY);
    if (isAppearance(saved)) return saved;

    const legacy = localStorage.getItem(LEGACY_PREFERENCES_KEY);
    if (legacy) {
      const value = JSON.parse(legacy) as { appearance?: unknown };
      if (isAppearance(value.appearance)) return value.appearance;
    }
  } catch {
    return "system";
  }
  return "system";
}

function applyAppearance(value: AppearancePreference) {
  const dark =
    value === "dark" ||
    (value === "system" &&
      window.matchMedia("(prefers-color-scheme: dark)").matches);
  document.documentElement.dataset.theme = "default";
  document.documentElement.dataset.colorScheme = dark ? "dark" : "light";
  document.documentElement.lang = "en-US";
}

type ThemeContextValue = {
  appearance: AppearancePreference;
  setAppearance: (value: AppearancePreference) => void;
};
const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [appearance, setAppearanceState] =
    useState<AppearancePreference>(readAppearance);
  const setAppearance = useCallback((value: AppearancePreference) => {
    localStorage.setItem(APPEARANCE_KEY, value);
    localStorage.removeItem(LEGACY_PREFERENCES_KEY);
    setAppearanceState(value);
    applyAppearance(value);
  }, []);

  useEffect(() => {
    applyAppearance(appearance);
    const color = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => applyAppearance(appearance);
    color.addEventListener("change", onChange);
    return () => color.removeEventListener("change", onChange);
  }, [appearance]);

  const value = useMemo(
    () => ({ appearance, setAppearance }),
    [appearance, setAppearance],
  );
  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme() {
  const value = useContext(ThemeContext);
  if (!value) throw new Error("useTheme must be used inside ThemeProvider");
  return value;
}
